import React, { useEffect, useState, useRef, useMemo } from 'react';
import './AlternativeOfferModal.css';
import CloseIcon from '../../../../src/icons/X.svg';
import GenericClock from '../../../../src/icons/GenericClock.svg';
import * as http from '../../../utils/http';
import * as translator from '../../../utils/translator';
import { ProductCard } from './ProductCard';
import Arrow from './Arrow/Arrow';

type ProductItem = {
	order_id: number;
	product_item_id: number;
	imageSrc: string;
	price: number;
	titleView: string;
	title: string;
	catalogPage: string;
	cannabisCatalogProductProperties: CannabisData;
};

type CannabisData = {
	thc: number | null;
	cbd: number | null;
	type: string | null;
	genetic: string | null;
	strain: string | null;
};

type ExpectedAlternativeProductData = {
	productItemID: number;
	price: number;
	thc: number;
	cbd: number;
	strain: string | null;
	titleView: string;
	imageSrc: string;
	catalogPage: string;
} | null;

type Props = {
	closeModal: () => void;
	language: string;
	setStickyNotificationDetails: any;
	orderProducts: ProductItem[]; // IMPORTANT: orderProducts is expected to be an array of length 1 or more, of only RX products (no OTC and cannavisID), all of which are out of stock.
	pharmacyAlternativeOffer: Record<number, ExpectedAlternativeProductData> | undefined;
	orderHashID: string;
	onAlternativeOfferMenuTriggerReject: () => void;
	onAlternativeOfferMenuTriggerAccept: () => void;
};

const PrescriptionRedemptionAlternativeOfferModal: React.FC<Props> = ({
	orderHashID,
	closeModal,
	setStickyNotificationDetails,
	onAlternativeOfferMenuTriggerReject,
	onAlternativeOfferMenuTriggerAccept,
	orderProducts,
	language,
	pharmacyAlternativeOffer,
}) => {
	const [isLoading, setIsLoading] = useState(true);

	const productAlternativePairs: [ProductItem, ExpectedAlternativeProductData][] = useMemo(() => {
		if (!pharmacyAlternativeOffer) return [];
		return Object.entries(pharmacyAlternativeOffer).map(([originalProductItemID, alternativeProductItemDTO]) => {
			return [orderProducts.find((product) => product.product_item_id === Number(originalProductItemID))!, alternativeProductItemDTO];
		});
	}, [pharmacyAlternativeOffer, orderProducts]);

	// Add state and refs for drag functionality
	const [dragDistance, setDragDistance] = useState(0);
	const [isClosing, setIsClosing] = useState(false);
	const dragStartY = useRef(0);
	const isDragging = useRef(false);
	const modalRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setIsLoading(false);
	}, []);

	// Handle touch start
	const handleTouchStart = (e: React.TouchEvent) => {
		dragStartY.current = e.touches[0].clientY;
		isDragging.current = true;
	};

	// Handle touch move
	const handleTouchMove = (e: React.TouchEvent) => {
		if (!isDragging.current) return;

		const currentY = e.touches[0].clientY;
		const distance = currentY - dragStartY.current;

		// Only allow dragging downwards
		if (distance < 0) {
			setDragDistance(0);
			return;
		}

		setDragDistance(distance);
	};

	// Handle touch end
	const handleTouchEnd = () => {
		isDragging.current = false;

		// If dragged more than 150px down, initiate smooth closing
		if (dragDistance > 150) {
			setIsClosing(true);
			// Slide the modal completely off screen
			setDragDistance(window.innerHeight);
			// Wait for animation to complete before actually closing
			setTimeout(closeModal, 600);
		} else {
			// Otherwise, snap back to original position
			setDragDistance(0);
		}
	};

	const acceptOffer = (_: React.FormEvent | null) => {
		setIsLoading(true);
		http
			.postToServer(`account/pharmacy-selection-view/${orderHashID}/accept-alternative`, {}, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response) => {
				setStickyNotificationDetails((currentDetails: any) => ({
					show: true,
					title: '',
					content: translator.translate(language, "my_account", `stickyNotifications/success/acceptExternalPharmacyAlternative`),
					level: 'success',
					showInfoIcon: false,
					refresh: !currentDetails.refresh,
				}));
				onAlternativeOfferMenuTriggerAccept?.();
			})
			.catch((error) => {
				setStickyNotificationDetails((currentDetails: any) => ({
					show: true,
					title: '',
					content: translator.translate(language, 'my_account', `stickyNotifications/error/error`),
					level: 'success',
					showInfoIcon: false,
					refresh: !currentDetails.refresh,
				}));
				window.clientLogger.error('Error submitting alternative resolution: ', error);
			})
			.finally(() => {
				setIsLoading(false);
				// closeDialog ? closeDialog() : null
			});
	};

	const rejectOffer = (_: React.FormEvent | null) => {
		setIsLoading(true);
		http
			.postToServer(`account/pharmacy-selection-view/${orderHashID}/reject-alternative`, {}, [http.MIDDLEWARES.CUSTOMER_ID])
			.then((response) => {
				onAlternativeOfferMenuTriggerReject?.();
			})
			.catch((error) => {
				setStickyNotificationDetails((currentDetails: any) => ({
					show: true,
					title: '',
					content: translator.translate(language, 'my_account', `stickyNotifications/error/error`),
					level: 'error',
					showInfoIcon: false,
					refresh: !currentDetails.refresh,
				}));
				window.clientLogger.error('Error submitting alternative resolution: ', error);
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	return (
		<div className="alternative-offer-modal-background" onClick={closeModal}>
			{isLoading && (
				<div className="loading-container">
					<div className="loading loading-circle"></div>
				</div>
			)}
			{productAlternativePairs && !isLoading && (
				<div
					ref={modalRef}
					id="alternative-offer-modal-container"
					className={`alternative-offer-modal-container ${isClosing ? 'closing' : ''}`}
					onClick={(e) => e.stopPropagation()}
					style={{
						transform: `translateY(${dragDistance}px)`,
						transition: isDragging.current ? 'none' : 'transform 0.6s cubic-bezier(0.2, 0.9, 0.1, 1)',
					}}
				>
					{/* header of modal */}
					<div className="alternative-offer-modal-header">
						{/* close button */}
						<button className="out-of-stock-close-button" onClick={closeModal}>
							<img src={CloseIcon} alt="Close" />
						</button>

						{/* drag bar used to close the modal in mobile layout */}
						<div className="open-close-drag-bar" onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}></div>

						{/* "Dringend: Bestell-Update" = Urgent: Order update - only shown in desktop layout */}
						<div className="top-attention-sign desktop-only">Dringend: Bestell-Update</div>
					</div>

					{/* body of modal - scrollable */}
					<div className="alternative-offer-modal-main-content">
						{/* "Dringend: Bestell-Update" = Urgent: Order update - only shown in mobile layout */}
						<div className="top-attention-sign mobile-only">Dringend: Bestell-Update</div>

						{/* top most title */}
						<h1 className="alternative-offer-modal-main-title">
							Die Apotheke hat uns soeben mitgeteilt, dass eines (oder mehrere) der von Ihnen bestellten Medikamente nicht mehr lieferbar ist.
						</h1>

						{/* Show these elements only if there's at least one alternative product */}
						{productAlternativePairs.some(([_, alternative]) => alternative !== null) && (
							<>
								{/* limited available box with clock icon */}
								<div className="out-of-stock-limited-availablity-box">
									<div className="limited-availablity-header">
										<div className="limited-availablity-icon">{<img src={GenericClock} alt="Generic Clock" />}</div>
										<p className="limited-availablity-title">Limited availability!</p>
									</div>

									<p className="limited-availablity-description">
										Stock is changing quickly and this alternative is not reserved for you. Act now to secure this product before it's gone!
									</p>
								</div>

								{/* instructions / invite to select from options */}
								<p className="out-of-stock-offer-description">Wir bieten Ihnen eine Alternative mit gleicher Genetik an. Bitte bestätigen.</p>
							</>
						)}

						{/* options area - scrollable*/}
						<div className="out-off-stock-all-offers">
							{productAlternativePairs.map(([originalProduct, alternativeOffer]) => {
								return (
									<div className="alternative-product-offer-container" key={originalProduct.product_item_id}>
										{/* "alternative for" title */}
										<p className="alternative-for">
											Alternative für
											<span className="alternative-for-original-product-name"> {originalProduct.titleView}</span>
										</p>

										{/* product cards + continuation arrow */}
										<div className="alternative-product-transition-container">
											{/* original product picked by the user */}
											<ProductCard
												cbd={originalProduct.cannabisCatalogProductProperties?.cbd}
												thc={originalProduct.cannabisCatalogProductProperties?.thc}
												strain={originalProduct.cannabisCatalogProductProperties?.strain}
												imageSrc={originalProduct.imageSrc}
												titleView={originalProduct.titleView}
												catalogPage={originalProduct.catalogPage}
												isOutOfStock={true}
											/>

											{/* continuation arrow: "out of stock" --> "alternative" */}
											<Arrow thickness={1} size={30} />

											{/* alternative offer product */}
											<ProductCard
												cbd={alternativeOffer!.cbd}
												thc={alternativeOffer!.thc}
												strain={alternativeOffer!.strain}
												imageSrc={alternativeOffer!.imageSrc}
												titleView={alternativeOffer!.titleView}
												catalogPage={alternativeOffer!.catalogPage}
											/>
										</div>
									</div>
								);
							})}
						</div>
					</div>

					{/* submit - only shown for orders with multiple items that are all out of stock, and if the customer has made a decision for all items */}
					<div className="confirmation-section flex flex-row gap-2">
						<button className="reject-offer-button" id="out-of-stock-reject-button" onClick={rejectOffer}>
							Apotheke wechseln
						</button>

						<button className="accept-offer-button" id="out-of-stock-accept-button" onClick={acceptOffer}>
							Alternative akzeptieren
						</button>
					</div>
				</div>
			)}
		</div>
	);
};

export default PrescriptionRedemptionAlternativeOfferModal;
